import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Container, Title, Text, Button, Paper, Stack, Group } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <>
          <Container 
            size="sm" 
            py="xl" 
            px="md"
            style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
            className="error-boundary-container"
          >
            <Paper 
              p="xl" 
              radius="md" 
              withBorder 
              shadow="sm" 
              w="100%"
              style={{ maxWidth: '100%' }}
              className="error-boundary-paper"
            >
              <Stack align="center" gap="lg" className="error-boundary-stack">
                <IconAlertTriangle 
                  size={64} 
                  color="red"
                  className="error-boundary-icon"
                />
                <Title 
                  order={2} 
                  ta="center"
                  className="error-boundary-title"
                >
                  Noget gik galt
                </Title>
                <Text 
                  ta="center" 
                  c="dimmed" 
                  size="lg"
                  lh={1.5}
                  className="error-boundary-text"
                >
                  Der opstod en uventet fejl. Prøv at genindlæse siden.
                </Text>
                {this.state.error && (
                  <Text 
                    ta="center" 
                    size="sm" 
                    c="red" 
                    fs="italic"
                    style={{ 
                      wordBreak: 'break-word',
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    {this.state.error.message}
                  </Text>
                )}
                <Group justify="center">
                  <Button 
                    onClick={this.handleReload}
                    leftSection={<IconRefresh size={16} />}
                    size="lg"
                    className="error-boundary-button"
                  >
                    Genindlæs siden
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Container>
          
          <style>{`
            /* Mobile-specific styles */
            @media (max-width: 768px) {
              .error-boundary-container {
                padding: 1rem !important;
              }
              
              .error-boundary-paper {
                padding: 1rem !important;
              }
              
              .error-boundary-stack {
                gap: 1rem !important;
              }
              
              .error-boundary-icon {
                width: 48px !important;
                height: 48px !important;
              }
              
              .error-boundary-title {
                font-size: 1.5rem !important;
              }
              
              .error-boundary-text {
                font-size: 1rem !important;
              }
              
              .error-boundary-button {
                width: 100% !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>
        </>
      );
    }

    return this.props.children;
  }
} 